import type Phaser from 'phaser';
import type { SimEvent } from '../../sim/simulation';
import type { TransitEvent } from '../../sim/transit';

/** Recorded sounds from the ZapSplat library (copied from lexicon-lair; GDD: Audio). */
export const AUDIO_FILES = {
  forest: 'audio/ambient/forest.mp3',
  crickets: 'audio/ambient/crickets-night.mp3',
  pickup: 'audio/sfx/item-pickup.mp3',
  hurt: 'audio/sfx/shield-block.mp3',
  laneBlocked: 'audio/sfx/lane-blocked.mp3',
} as const;

type Wave = OscillatorType;

/**
 * A world's background sound: recorded (the jungle's forest, Earth's night crickets) or made here
 * (Mars's wind; on the airless Moon only the suit's own faint hum).
 */
export type Ambience = 'forest' | 'crickets' | 'wind' | 'void';

const RECORDED: readonly Ambience[] = ['forest', 'crickets'];

/**
 * The made ambience playing now. Shared by every scene's Sfx (they all use the game's one audio
 * context), so it carries on across room changes the way the recorded ones do.
 */
let madeAmbience: { kind: Ambience; loop: Loop | null } | null = null;

/**
 * One volume every Sfx plays through, per audio context: the pause menu mutes it, which silences
 * the jetpack and the made ambience too (whichever scene's Sfx started them).
 */
const masters = new WeakMap<AudioContext, GainNode>();

function masterFor(ctx: AudioContext): GainNode {
  let master = masters.get(ctx);
  if (!master) {
    master = ctx.createGain();
    master.connect(ctx.destination);
    masters.set(ctx, master);
  }
  return master;
}

/** Pauses every sound: the recorded ones in the sound manager and the synthesized ones here. */
export function setSoundPaused(scene: Phaser.Scene, paused: boolean): void {
  if (paused) scene.sound.pauseAll();
  else scene.sound.resumeAll();
  const manager = scene.sound as Phaser.Sound.WebAudioSoundManager;
  if (!('context' in manager)) return;
  const ctx = manager.context;
  masterFor(ctx).gain.setTargetAtTime(paused ? 0 : 1, ctx.currentTime, 0.02);
}

interface Loop {
  src: AudioBufferSourceNode;
  gain: GainNode;
}

/**
 * Game sounds. The library has no sci-fi sounds yet, so lasers, jets and swooshes are synthesized
 * with Web Audio (the GDD's stopgap); recorded files cover pickups, hits and some ambience.
 */
export class Sfx {
  private readonly ctx: AudioContext | null;
  private readonly out: GainNode | null;
  private noise: AudioBuffer | null = null;
  private jet: Loop | null = null;
  private hum: Loop | null = null;

  constructor(private readonly scene: Phaser.Scene) {
    const manager = scene.sound as Phaser.Sound.WebAudioSoundManager;
    this.ctx = 'context' in manager ? manager.context : null;
    if (this.ctx) {
      this.out = this.ctx.createGain();
      this.out.gain.value = 0.35;
      this.out.connect(masterFor(this.ctx));
      this.noise = this.makeNoise(this.ctx);
    } else {
      this.out = null;
    }
  }

  /**
   * Starts a world's ambience (browsers allow audio only after the first touch or key), and
   * stops any other. The same one carries on across room changes instead of restarting.
   */
  startAmbience(kind: Ambience): void {
    for (const other of RECORDED) if (other !== kind) this.scene.sound.removeByKey(other);
    if (madeAmbience && madeAmbience.kind !== kind) this.stopMadeAmbience();
    if (RECORDED.includes(kind)) {
      if (!this.scene.sound.getAll(kind).some((s) => s.isPlaying)) {
        this.scene.sound.add(kind, { loop: true, volume: 0.3 }).play();
      }
    } else if (!madeAmbience) {
      const loop = kind === 'wind' ? this.windLoop() : this.noiseLoop(110, 0.05);
      madeAmbience = { kind, loop };
    }
  }

  /** Stops the world's ambience (leaving a planet), so the next one starts fresh. */
  stopAmbience(): void {
    for (const kind of RECORDED) this.scene.sound.removeByKey(kind);
    this.stopMadeAmbience();
  }

  private stopMadeAmbience(): void {
    this.stopLoop(madeAmbience?.loop ?? null);
    madeAmbience = null;
  }

  /** The low rumble of light speed: space's ambience until real recordings exist. */
  startHum(): void {
    this.hum ??= this.noiseLoop(160, 0.14);
  }

  /** Stops this scene's own synthesized loops (the jetpack, the transit's hum) when it ends. */
  stopAll(): void {
    this.stopJet();
    this.stopLoop(this.hum);
    this.hum = null;
  }

  /** Lab menu sounds: moving the selection, a purchase, and a refusal. */
  ui(kind: 'select' | 'buy' | 'deny' | 'launch'): void {
    if (kind === 'select') this.tone('square', 900, 900, 0.04, 0.05);
    else if (kind === 'buy') {
      this.tone('triangle', 660, 1320, 0.18, 0.12);
      this.scene.sound.play('pickup', { volume: 0.4 });
    } else if (kind === 'deny') this.scene.sound.play('laneBlocked', { volume: 0.4 });
    else this.sweep(300, 2400, 0.7);
  }

  /** Transit sounds. Autofire is a soft tick, so it can run the whole flight. */
  playTransit(events: readonly TransitEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case 'fire':
          this.tone('square', 1600, 900, 0.03, 0.025);
          break;
        case 'rockHit':
          this.tone('square', 220, 140, 0.04, 0.06);
          break;
        case 'rockBroken':
          this.burst(e.size === 'big' ? 0.35 : 0.18, e.size === 'big' ? 600 : 1200, 0.4);
          break;
        case 'raiderHit':
          this.tone('square', 500, 250, 0.05, 0.08);
          break;
        case 'raiderKilled':
          this.burst(0.3, 900, 0.45);
          this.tone('sawtooth', 500, 70, 0.3, 0.1);
          break;
        case 'boltFire':
          this.tone('square', 900, 300, 0.12, 0.06);
          break;
        case 'pickup':
          this.scene.sound.play('pickup', { volume: 0.5 });
          break;
        case 'hurt':
          this.scene.sound.play('hurt', { volume: 0.6 });
          break;
        case 'rewind':
          this.stopAll();
          this.tone('sine', 900, 80, 1.1, 0.25);
          break;
        case 'arrived':
          this.sweep(300, 2400, 0.9);
          break;
      }
    }
  }

  play(events: readonly SimEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case 'jump':
          this.tone('square', 260, 620, 0.09, 0.12);
          break;
        case 'land':
          this.burst(0.05, 300, 0.25);
          break;
        case 'fire':
          this.tone('square', 1200, 260, 0.08, 0.1);
          break;
        case 'rocketReady':
          this.tone('triangle', 880, 1320, 0.08, 0.12);
          break;
        case 'rocketFire':
          this.burst(0.2, 1400, 0.3);
          this.tone('sawtooth', 320, 110, 0.22, 0.1);
          break;
        case 'blast':
          this.burst(0.5, 700, 0.6);
          break;
        case 'dash':
          this.sweep(600, 2600, 0.16);
          break;
        case 'enemyHit':
          this.tone('square', 180, 90, 0.06, 0.15);
          break;
        case 'enemyKilled':
          this.burst(0.25, 900, 0.45);
          this.tone('sawtooth', 400, 60, 0.25, 0.12);
          break;
        case 'enemyBlocked':
          this.tone('triangle', 2400, 1800, 0.06, 0.08);
          break;
        case 'droneAim':
          this.tone('sine', 300, 900, 0.55, 0.06);
          break;
        case 'boltFire':
          this.tone('square', 900, 300, 0.12, 0.07);
          break;
        case 'bossTell':
          this.tone('sawtooth', 700, 1400, 0.25, 0.1);
          break;
        case 'bossHop':
          this.sweep(400, 2200, 0.2);
          break;
        case 'bossCharge':
          this.burst(0.4, 400, 0.4);
          this.tone('sawtooth', 120, 60, 0.4, 0.12);
          break;
        case 'bossSlam':
          this.burst(0.35, 500, 0.7);
          break;
        case 'bossPhase':
          this.tone('square', 220, 660, 0.35, 0.12, 10);
          break;
        case 'bossDefeated':
          this.burst(0.9, 900, 0.7);
          this.tone('sine', 600, 40, 1.2, 0.2);
          break;
        case 'pickup':
          this.scene.sound.play('pickup', { volume: 0.5 });
          break;
        case 'hurt':
          this.scene.sound.play('hurt', { volume: 0.6 });
          break;
        case 'laneSwitch':
          this.sweep(e.to > e.from ? 1800 : 500, e.to > e.from ? 500 : 1800, 0.22);
          break;
        case 'laneBlocked':
          this.scene.sound.play('laneBlocked', { volume: 0.4 });
          break;
        case 'ventEnter':
          this.sweep(250, 1500, 0.6);
          break;
        case 'radiation':
          this.tone('square', 1800, 1700, 0.08, 0.06);
          break;
        case 'radiationBurn':
          this.burst(0.3, 4000, 0.3);
          break;
        case 'modFitted':
          this.tone('triangle', 520, 1560, 0.35, 0.14);
          this.tone('square', 1040, 2080, 0.2, 0.05);
          break;
        case 'modSalvaged':
          this.tone('square', 500, 220, 0.18, 0.08);
          break;
        case 'flutterStart':
          this.tone('triangle', 500, 700, 0.3, 0.12, 14);
          break;
        case 'jetStart':
          this.startJet();
          break;
        case 'jetStop':
          this.stopJet();
          break;
        case 'rewind':
          this.stopJet();
          this.tone('sine', 900, 80, 1.1, 0.25);
          break;
        case 'fell':
          this.tone('sine', 600, 120, 0.4, 0.2);
          break;
      }
    }
  }

  private tone(
    wave: Wave,
    from: number,
    to: number,
    seconds: number,
    volume: number,
    wobble = 0,
  ): void {
    const { ctx, out } = this;
    if (!ctx || !out) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = wave;
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t + seconds);
    if (wobble > 0) {
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = wobble;
      depth.gain.value = 60;
      lfo.connect(depth).connect(osc.frequency);
      lfo.start(t);
      lfo.stop(t + seconds);
    }
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + seconds);
    osc.connect(gain).connect(out);
    osc.start(t);
    osc.stop(t + seconds);
  }

  /**
   * Geiger clicks, at random, about a dozen a second while the suit stands in radiation it isn't
   * shielded for. Call every frame the game runs.
   */
  geiger(dtSeconds: number, exposed: boolean): void {
    if (exposed && Math.random() < dtSeconds * 12) this.burst(0.012, 7000, 0.35);
  }

  /** A short filtered noise hit (landings, explosions). */
  private burst(seconds: number, cutoff: number, volume: number): void {
    const { ctx, out, noise } = this;
    if (!ctx || !out || !noise) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + seconds);
    src.connect(filter).connect(gain).connect(out);
    src.start(t);
    src.stop(t + seconds);
  }

  /** A whoosh: noise through a band-pass filter sweeping between two frequencies. */
  private sweep(from: number, to: number, seconds: number): void {
    const { ctx, out, noise } = this;
    if (!ctx || !out || !noise) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 3;
    filter.frequency.setValueAtTime(from, t);
    filter.frequency.exponentialRampToValueAtTime(to, t + seconds);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.exponentialRampToValueAtTime(0.5, t + seconds * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, t + seconds);
    src.connect(filter).connect(gain).connect(out);
    src.start(t);
    src.stop(t + seconds);
  }

  private startJet(): void {
    this.jet ??= this.noiseLoop(700, 0.18);
  }

  private stopJet(): void {
    this.stopLoop(this.jet);
    this.jet = null;
  }

  /** Looping low-passed noise that fades in (the jetpack, the light-speed hum). */
  private noiseLoop(cutoff: number, volume: number): Loop | null {
    const { ctx, out, noise } = this;
    if (!ctx || !out || !noise) return null;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + 0.06);
    src.connect(filter).connect(gain).connect(out);
    src.start();
    return { src, gain };
  }

  /** Wind: band-passed noise whose loudness and pitch drift slowly, in gusts. */
  private windLoop(): Loop | null {
    const { ctx, out, noise } = this;
    if (!ctx || !out || !noise) return null;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 420;
    filter.Q.value = 0.8;
    // Two slow oscillators: one swells the gusts, one moves their pitch. The gusts shape a gain
    // of their own, so the fade-in and stopLoop's fade-out stay clean on the outer one.
    const gusts = ctx.createGain();
    gusts.gain.value = 1;
    const gust = ctx.createOscillator();
    gust.frequency.value = 0.13;
    const gustDepth = ctx.createGain();
    gustDepth.gain.value = 0.5;
    gust.connect(gustDepth).connect(gusts.gain);
    const sweep = ctx.createOscillator();
    sweep.frequency.value = 0.07;
    const sweepDepth = ctx.createGain();
    sweepDepth.gain.value = 180;
    sweep.connect(sweepDepth).connect(filter.frequency);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.6);
    src.connect(filter).connect(gusts).connect(gain).connect(out);
    src.start();
    gust.start();
    sweep.start();
    // The oscillators end with the noise.
    src.onended = () => {
      gust.stop();
      sweep.stop();
    };
    return { src, gain };
  }

  private stopLoop(loop: Loop | null): void {
    const { ctx } = this;
    if (!ctx || !loop) return;
    loop.gain.gain.cancelScheduledValues(ctx.currentTime);
    loop.gain.gain.setValueAtTime(loop.gain.gain.value, ctx.currentTime);
    loop.gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    loop.src.stop(ctx.currentTime + 0.1);
  }

  private makeNoise(ctx: AudioContext): AudioBuffer {
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }
}
